<template>
    <div>
        <div class="flex items-center manage-columns-header" v-if="showHeaderContainer">
            {{$t('datatable.manage.columns.header')}}
        </div>
        <div class="flex flex-col sm:flex-row">
            <manage-columns-section
                key="TO_HIDE"
                :availableColumns="showActiveColumns"
                :displayQueueAsRows="displayQueueAsRows"
                @onChange="(evt) => onColumnChange(evt , sectionsToManage.TO_HIDE)">
                <template v-slot:search>
                    <div class="w-4/6 mx-2">
                        <el-input :placeholder="$t('datatable.manage.columns.search')"
                                  v-model="filterActiveColumns"
                                  suffix-icon="el-icon-search" class="search-columns"/>
                    </div>
                </template>
                <template v-slot:button="{columns, allChecked}">
                    <div :class="getClass">
                        <el-button :disabled="!showActiveColumns.length || !columns.length" class="w-24" type="danger"
                                   size="small"
                                   @click="removeColumns(columns)">
                            <span class="text-white">
                                {{allChecked ? $t('datatable.manage.columns.remove.all') :
                                $t('datatable.manage.columns.remove')}}
                            </span>
                        </el-button>
                    </div>
                </template>
            </manage-columns-section>
            <manage-columns-section
                key="TO_SHOW"
                :availableColumns="showAvailableColumns"
                :displayQueueAsRows="displayQueueAsRows"
                @onChange="(evt) => onColumnChange(evt , sectionsToManage.TO_SHOW)">
                <template v-slot:search>
                    <div class="w-4/6 mx-2">
                        <el-input :placeholder="$t('datatable.manage.columns.search')"
                                  v-model="filterAvailableColumns"
                                  suffix-icon="el-icon-search" class="search-columns"/>
                    </div>
                </template>
                <template v-slot:button="{columns, allChecked}">
                    <el-button :disabled="!showAvailableColumns.length || !columns.length" class="w-24" type="success"
                               size="small"
                               @click="addColumnInBulk(columns)">
                        {{allChecked ? $t('datatable.manage.columns.add.all') : $t('datatable.manage.columns.add')}}
                    </el-button>
                </template>
            </manage-columns-section>
        </div>
    </div>
</template>
<script>
    import xor from 'lodash/xor'
    import get from 'lodash/get'
    import { sectionsToManage } from '@/enum/dataTable'
    import draggableEvents from '@/enum/draggableEvents'

    export default {
        components: {
            ManageColumnsSection: () => import('./ManageColumnsSection')
        },
        props: {
            availableColumns: {
                type: Array,
                default: () => [],
            },
            visibleColumns: {
                type: Array,
                default: () => [],
            },
            showHeaderContainer: {
                type: Boolean,
                default: true,
            },
            displayQueueAsRows: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                filterAvailableColumns: '',
                filterActiveColumns: '',
                allColumnsValue: [],
                valueToRemove: [],
                valueToAdd: [],
                activeColumns: [],
                unselectedColumns: [],
                isIndeterminate: false,
                sectionsToManage,
            }
        },
        computed: {
            showAvailableColumns() {
                return this.filterAvailableColumns ? this.filteredColumns : this.unselectedColumns
            },
            getClass() {
                if (this.activeColumns.length === 0) {
                    if (this.$rtl.isRTL) {
                        return 'mr-auto'
                    } else {
                        return 'ml-auto'
                    }
                }
                return ''
            },
            showActiveColumns() {
                return this.filterActiveColumns ? this.filteredAvailableColumns : this.activeColumns
            },
            filteredColumns() {
                return this.unselectedColumns.filter(el => {
                    return el.label.toLowerCase().includes(this.filterAvailableColumns.toLowerCase())
                })
            },
            filteredAvailableColumns() {
                return this.activeColumns.filter(el => {
                    return el.label.toLowerCase().includes(this.filterActiveColumns.toLowerCase())
                })
            }
        },
        methods: {
            addColumnInBulk(columns) {
                let itemToRemove = this.unselectedColumns.filter(c => columns.includes(c.prop))
                let remainingItems = this.unselectedColumns.filter(c => !columns.includes(c.prop))
                this.activeColumns = this.activeColumns.concat([...itemToRemove])
                this.unselectedColumns = remainingItems

                this.valueToRemove = this.activeColumns.map(c => c.prop)
                this.valueToAdd = []

                let newColumns = this.activeColumns.map(c => c.prop)
                this.$emit('on-change-visibility', newColumns)
            },
            removeColumns(columns) {
                let itemToRemove = this.activeColumns.filter(c => columns.includes(c.prop))
                let remainingItems = this.activeColumns.filter(c => !columns.includes(c.prop))
                this.unselectedColumns = this.unselectedColumns.concat([...itemToRemove])
                this.activeColumns = remainingItems

                this.valueToRemove = this.activeColumns.map(c => c.prop)

                let newColumns = this.activeColumns.map(c => c.prop)
                this.$emit('on-change-visibility', newColumns)
            },
            addColumns(column) {
                const columnProp = get(column, '[0].prop', '')
                this.activeColumns = this.activeColumns.filter(c => c.prop !== columnProp)
                this.valueToRemove = this.activeColumns.map(c => c.prop)

                let newColumns = this.activeColumns.map(c => c.prop)

                this.$emit('on-change-visibility', newColumns)
            },
            onColumnChange(evt, section) {
                // DOCS: This event is called with one argument containing one of the following properties: added/removed/moved
                let action = get(Object.keys(evt), 0)
                if (action === draggableEvents.REMOVED) return

                let eventData = evt[action]
                let column = get(eventData, 'element')

                if (action === draggableEvents.MOVED && section === sectionsToManage.TO_HIDE) {
                    this.$emit('on-reorder-column', eventData)
                } else {
                    this[section]([column])
                }
            },
            initData() {
                this.activeColumns = this.availableColumns.filter(c => this.visibleColumns.includes(c.prop))
                this.unselectedColumns = xor(this.availableColumns, this.activeColumns)
                this.allColumnsValue = this.activeColumns.map(c => c.prop)
                this.valueToRemove = this.allColumnsValue
            },
            handleCheckAllChange(checked, section) {
                this[section] = checked ? this.allColumnsValue : []
                this.isIndeterminate = false
            },
        },
        watch: {
            visibleColumns: {
                immediate: true,
                handler: function () {
                    this.initData()
                },
            },
            availableColumns() {
                this.initData()
            },
        },
    }
</script>
<style scoped lang="scss">
    .manage-columns-header {
        height: 40px;
        padding: 10px 20px;
        box-shadow: 0 1px 0 0 var(--silver);
        background-color: var(--mainBgColor-color);
        font-size: var(--text-main-sm);
        color: var(--mainTextColor-color);
    }
</style>
